import { useCallback, useState } from 'react';

/**
 * The user's own menu settings, kept in this browser: which entries they pinned, and whether the
 * menu is folded to icons. Neither is worth a round trip, and both are harmless to lose.
 */
const PINS_PREFIX = 'ui.nav.pins.';
const COLLAPSED_KEY = 'ui.nav.collapsed';

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private mode or a full quota: the setting just lasts until the tab closes.
  }
}

function readPins(userId: string | null, defaults: readonly string[]): string[] {
  const stored = userId ? read(`${PINS_PREFIX}${userId}`) : null;
  if (stored === null) {
    return [...defaults];
  }
  try {
    const parsed: unknown = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [...defaults];
  }
}

/** Pinned entry ids, per user; `defaults` until the user pins or unpins anything. */
export function usePinnedItems(
  userId: string | null,
  defaults: readonly string[],
): { pinned: readonly string[]; toggle: (id: string) => void } {
  const [state, setState] = useState(() => ({ userId, pinned: readPins(userId, defaults) }));
  // A different user in the same tab starts from their own list.
  const pinned = state.userId === userId ? state.pinned : readPins(userId, defaults);
  const toggle = useCallback(
    (id: string) => {
      const next = pinned.includes(id) ? pinned.filter(entry => entry !== id) : [...pinned, id];
      if (userId) {
        write(`${PINS_PREFIX}${userId}`, JSON.stringify(next));
      }
      setState({ userId, pinned: next });
    },
    [pinned, userId],
  );
  return { pinned, toggle };
}

/** Whether the desktop menu is folded to its icon rail. */
export function useCollapsedMenu(): [boolean, (collapsed: boolean) => void] {
  const [collapsed, setCollapsed] = useState(() => read(COLLAPSED_KEY) === '1');
  const update = useCallback((value: boolean) => {
    write(COLLAPSED_KEY, value ? '1' : '0');
    setCollapsed(value);
  }, []);
  return [collapsed, update];
}
